import * as THREE from 'three';

import { EyesId, HatId, MouthId, NeckId, VineId } from '../catalog';

/*
 * Procedural geometry for the tomato's body, vines and accessories. Everything is built from
 * primitives so there are no model files to load or license. The tomato faces +Z.
 */

export const BODY_Y_SCALE = 0.9;
/** Height of the tomato's top at the dimple, where the vine and hats attach. */
export const TOP_Y = 0.78;

/** z of the tomato surface at (x, y), for sticking features onto the front. */
export const surfaceZ = (x: number, y: number): number =>
    Math.sqrt(Math.max(0, 1 - x * x - (y / BODY_Y_SCALE) ** 2));

const std = (
    color: number | string,
    extra: THREE.MeshStandardMaterialParameters = {}
) =>
    new THREE.MeshStandardMaterial({
        color,
        roughness: 0.55,
        metalness: 0,
        ...extra,
    });

const gloss = (
    color: number | string,
    extra: THREE.MeshPhysicalMaterialParameters = {}
) =>
    new THREE.MeshPhysicalMaterial({
        color,
        roughness: 0.25,
        clearcoat: 0.8,
        clearcoatRoughness: 0.2,
        ...extra,
    });

const GREEN = 0x4cae3a;
const DARK_GREEN = 0x2f7d2a;

/** A slightly squashed, lobed sphere with a dimple on top and a shaded vertex-colour gradient. */
export const createBodyGeometry = (): THREE.BufferGeometry => {
    let g: THREE.BufferGeometry = new THREE.SphereGeometry(1, 96, 64);
    g.deleteAttribute('uv');
    g.deleteAttribute('normal');
    // Weld the seam so the recomputed normals are smooth all the way round.
    g = mergeByPosition(g);

    const p = g.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(p.count * 3);
    const v = new THREE.Vector3();
    for (let i = 0; i < p.count; i++) {
        v.fromBufferAttribute(p, i);
        const r = Math.hypot(v.x, v.z);
        const ang = Math.atan2(v.z, v.x);
        const lobe = Math.cos(ang * 5);
        const lobeAmount = 1 - v.y * v.y;
        const radial = 1 + 0.035 * lobe * lobeAmount;
        v.x *= radial;
        v.z *= radial;
        v.y *= BODY_Y_SCALE;
        if (v.y > 0) v.y -= 0.17 * Math.exp(-(r * r) / 0.1);
        else v.y += 0.05 * Math.exp(-(r * r) / 0.08);
        p.setXYZ(i, v.x, v.y, v.z);

        // Darker in the creases between lobes and near the stem, lighter on the shoulders.
        const shade =
            0.86 +
            0.14 * (lobe * 0.5 + 0.5) * Math.min(1, lobeAmount * 2) -
            0.12 * Math.exp(-(r * r) / 0.12) * (v.y > 0 ? 1 : 0);
        colors[i * 3] = shade;
        colors[i * 3 + 1] = shade;
        colors[i * 3 + 2] = shade;
    }
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    g.computeVertexNormals();
    return g;
};

/** Merges vertices at identical positions (the sphere's seam and poles), keeping the index. */
function mergeByPosition(g: THREE.BufferGeometry): THREE.BufferGeometry {
    const p = g.attributes.position as THREE.BufferAttribute;
    const index = g.index!;
    const map = new Map<string, number>();
    const remap = new Uint32Array(p.count);
    const positions: number[] = [];
    for (let i = 0; i < p.count; i++) {
        const key = `${p.getX(i).toFixed(4)},${p.getY(i).toFixed(4)},${p.getZ(i).toFixed(4)}`;
        let id = map.get(key);
        if (id === undefined) {
            id = positions.length / 3;
            map.set(key, id);
            positions.push(p.getX(i), p.getY(i), p.getZ(i));
        }
        remap[i] = id;
    }
    const indices: number[] = [];
    for (let i = 0; i < index.count; i += 3) {
        const a = remap[index.getX(i)];
        const b = remap[index.getX(i + 1)];
        const c = remap[index.getX(i + 2)];
        if (a !== b && b !== c && a !== c) indices.push(a, b, c); // drops the degenerate pole triangles
    }
    const out = new THREE.BufferGeometry();
    out.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3)
    );
    out.setIndex(indices);
    g.dispose();
    return out;
}

const leafShape = (() => {
    const s = new THREE.Shape();
    s.moveTo(0, 0);
    s.bezierCurveTo(0.17, 0.08, 0.2, 0.34, 0, 0.62);
    s.bezierCurveTo(-0.2, 0.34, -0.17, 0.08, 0, 0);
    return s;
})();

const leafGeometry = new THREE.ShapeGeometry(leafShape, 8);

const makeLeaf = (color = GREEN, scale = 1): THREE.Mesh => {
    const m = new THREE.Mesh(
        leafGeometry,
        std(color, { side: THREE.DoubleSide, roughness: 0.6 })
    );
    m.scale.setScalar(scale);
    return m;
};

const tube = (
    points: THREE.Vector3[],
    radius: number,
    color = DARK_GREEN,
    segments = 64
) => {
    const curve = new THREE.CatmullRomCurve3(points);
    return new THREE.Mesh(
        new THREE.TubeGeometry(curve, segments, radius, 8, false),
        std(color, { roughness: 0.5 })
    );
};

const calyx = (leaves: number, length: number, color = GREEN): THREE.Group => {
    const g = new THREE.Group();
    const hub = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 20, 12),
        std(DARK_GREEN)
    );
    hub.scale.y = 0.45;
    g.add(hub);
    for (let i = 0; i < leaves; i++) {
        const holder = new THREE.Group();
        holder.rotation.y = (i / leaves) * Math.PI * 2 + (i % 2) * 0.1;
        const leaf = makeLeaf(i % 2 ? color : DARK_GREEN + 0x0a1a0a, length);
        leaf.rotation.x = Math.PI / 2 + 0.04;
        leaf.position.y = 0.06;
        holder.add(leaf);
        g.add(holder);
    }
    return g;
};

/** A curl that spirals in a plane, for tendrils. */
const curlPoints = (
    radius: number,
    turns: number,
    rise: number,
    steps = 24
): THREE.Vector3[] => {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const a = t * turns * Math.PI * 2;
        const r = radius * (1 - t * 0.75);
        pts.push(
            new THREE.Vector3(
                Math.cos(a) * r - radius,
                t * rise + Math.sin(a) * r * 0.3,
                Math.sin(a) * r
            )
        );
    }
    return pts;
};

const flower = (scale = 1): THREE.Group => {
    const g = new THREE.Group();
    const petalGeo = new THREE.SphereGeometry(0.05, 12, 8);
    const petalMat = std(0xffd93d, { roughness: 0.4 });
    for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const petal = new THREE.Mesh(petalGeo, petalMat);
        petal.position.set(Math.cos(a) * 0.065, Math.sin(a) * 0.065, 0);
        petal.scale.set(1.3, 0.9, 0.5);
        petal.rotation.z = a;
        g.add(petal);
    }
    const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.035, 10, 8),
        std(0xe67e22)
    );
    eye.position.z = 0.015;
    g.add(eye);
    g.scale.setScalar(scale);
    return g;
};

export interface VineBuild {
    group: THREE.Group;
    /** Parts that would collide with a hat, hidden while one is worn. */
    stem: THREE.Object3D[];
    /** Called each frame with elapsed seconds, for sway. */
    animate: (t: number) => void;
}

export const buildVine = (kind: VineId): VineBuild => {
    const group = new THREE.Group();
    const stem: THREE.Object3D[] = [];
    let animate: (t: number) => void = () => undefined;

    if (kind === 'classic') {
        const cal = calyx(6, 1.3);
        cal.position.y = TOP_Y + 0.03;
        group.add(cal);
        const stemMesh = tube(
            [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0.02, 0.14, 0),
                new THREE.Vector3(0.1, 0.27, 0),
                new THREE.Vector3(0.2, 0.33, 0),
            ],
            0.045,
            DARK_GREEN,
            24
        );
        stemMesh.position.y = TOP_Y + 0.05;
        group.add(stemMesh);
        stem.push(stemMesh);
        const curl = tube(curlPoints(0.11, 1.6, 0.14), 0.014, GREEN, 40);
        curl.position.set(0.0, TOP_Y + 0.2, 0.0);
        curl.rotation.y = 0.6;
        group.add(curl);
        stem.push(curl);
        animate = (t) => {
            cal.rotation.z = Math.sin(t * 1.3) * 0.01;
            curl.rotation.x = Math.sin(t * 2.1) * 0.12;
        };
    } else if (kind === 'ivy') {
        const cal = calyx(5, 1.05);
        cal.position.y = TOP_Y + 0.03;
        group.add(cal);
        // A vine that spirals down the front, wrapped just outside the surface.
        const pts: THREE.Vector3[] = [];
        const turns = 2.2;
        for (let i = 0; i <= 90; i++) {
            const t = i / 90;
            const y = TOP_Y - 0.1 - t * 1.45;
            const rr =
                Math.sqrt(Math.max(0.02, 1 - (y / BODY_Y_SCALE) ** 2)) + 0.025;
            const a = Math.PI * 0.3 + t * turns * Math.PI * 2;
            pts.push(new THREE.Vector3(Math.cos(a) * rr, y, Math.sin(a) * rr));
        }
        const spiral = tube(pts, 0.022, DARK_GREEN, 180);
        group.add(spiral);
        for (let i = 4; i < 90; i += 6) {
            const leaf = makeLeaf(i % 12 === 4 ? GREEN : 0x63c04a, 0.32);
            const p = pts[i];
            leaf.position.copy(p);
            // Lie flat on the tomato: face outward along the surface normal, point away down the vine.
            const n = new THREE.Vector3(
                p.x,
                p.y / (BODY_Y_SCALE * BODY_Y_SCALE),
                p.z
            ).normalize();
            leaf.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
            leaf.rotateZ(i * 0.9);
            group.add(leaf);
        }
        const stemMesh = tube(
            [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(-0.02, 0.15, 0),
                new THREE.Vector3(-0.1, 0.26, 0.02),
            ],
            0.04,
            DARK_GREEN,
            16
        );
        stemMesh.position.y = TOP_Y + 0.05;
        group.add(stemMesh);
        stem.push(stemMesh);
        animate = (t) => {
            spiral.rotation.y = Math.sin(t * 0.8) * 0.02;
        };
    } else {
        const cal = calyx(7, 1.15, 0x58b848);
        cal.position.y = TOP_Y + 0.03;
        group.add(cal);
        const stemMesh = tube(
            [
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0, 0.15, 0),
                new THREE.Vector3(0.05, 0.3, 0),
                new THREE.Vector3(0.0, 0.42, 0.02),
            ],
            0.04,
            DARK_GREEN,
            24
        );
        stemMesh.position.y = TOP_Y + 0.05;
        group.add(stemMesh);
        stem.push(stemMesh);
        const top = flower(1.5);
        top.position.set(0.0, TOP_Y + 0.5, 0.03);
        group.add(top);
        stem.push(top);
        // Hanging tendrils with little flowers.
        const tendrils: THREE.Object3D[] = [];
        [-1, 1].forEach((side, i) => {
            const t = tube(curlPoints(0.1, 1.8, -0.18, 28), 0.012, GREEN, 40);
            t.position.set(side * 0.55, TOP_Y - 0.12, 0.45);
            t.rotation.set(0.2, side * 0.8, side * 0.5);
            group.add(t);
            tendrils.push(t);
            const fl = flower(0.9);
            fl.position.set(side * 0.78, TOP_Y - 0.05 - i * 0.05, 0.6);
            fl.rotation.y = side * 0.5;
            group.add(fl);
        });
        animate = (t) => {
            tendrils.forEach(
                (m, i) => (m.rotation.z += Math.sin(t * 2 + i) * 0.0008)
            );
            top.rotation.z = Math.sin(t * 1.5) * 0.15;
        };
    }
    return { group, stem, animate };
};

export interface FeatureBuild {
    group: THREE.Group;
    /** googly eyes: the pupils that should wobble and look at the pointer. */
    pupils?: THREE.Object3D[];
    /** hearts: parts that pulse. */
    pulse?: THREE.Object3D[];
}

const EYE_X = 0.34;
const EYE_Y = 0.2;

const googlyEye = (side: number): { eye: THREE.Group; pupil: THREE.Mesh } => {
    const eye = new THREE.Group();
    const white = new THREE.Mesh(
        new THREE.SphereGeometry(0.17, 28, 20),
        gloss(0xffffff, { roughness: 0.2 })
    );
    white.scale.z = 0.5;
    eye.add(white);
    const pupil = new THREE.Mesh(
        new THREE.SphereGeometry(0.075, 20, 14),
        std(0x111111, { roughness: 0.2 })
    );
    pupil.position.z = 0.065;
    pupil.scale.z = 0.6;
    eye.add(pupil);
    eye.position.set(side * EYE_X, EYE_Y, surfaceZ(side * EYE_X, EYE_Y) + 0.03);
    eye.lookAt(eye.position.clone().multiplyScalar(2)); // rim follows the surface normal
    eye.userData.faceNormal = true;
    return { eye, pupil };
};

const heartShape = (() => {
    const s = new THREE.Shape();
    s.moveTo(0, -0.5);
    s.bezierCurveTo(-0.15, -0.35, -0.55, -0.1, -0.5, 0.2);
    s.bezierCurveTo(-0.46, 0.5, -0.1, 0.55, 0, 0.3);
    s.bezierCurveTo(0.1, 0.55, 0.46, 0.5, 0.5, 0.2);
    s.bezierCurveTo(0.55, -0.1, 0.15, -0.35, 0, -0.5);
    return s;
})();

export const buildEyes = (kind: EyesId): FeatureBuild => {
    const group = new THREE.Group();
    if (kind === 'googly') {
        const pupils: THREE.Object3D[] = [];
        [-1, 1].forEach((side) => {
            const { eye, pupil } = googlyEye(side);
            group.add(eye);
            pupils.push(pupil);
        });
        return { group, pupils };
    }
    if (kind === 'angry') {
        const pupils: THREE.Object3D[] = [];
        const browMat = std(0x2a1010, { roughness: 0.6 });
        [-1, 1].forEach((side) => {
            const { eye, pupil } = googlyEye(side);
            // The brow slants down towards the nose and clips the top of the eye into a glare.
            const brow = new THREE.Mesh(
                new THREE.CapsuleGeometry(0.045, 0.26, 4, 10),
                browMat
            );
            brow.rotation.z = Math.PI / 2 + side * 0.5;
            brow.position.set(-side * 0.01, 0.13, 0.1);
            brow.scale.z = 0.7;
            eye.add(brow);
            group.add(eye);
            pupils.push(pupil);
        });
        return { group, pupils };
    }
    if (kind === 'shades') {
        const lensMat = gloss(0x0b0b10, {
            roughness: 0.05,
            clearcoat: 1,
            metalness: 0.3,
        });
        [-1, 1].forEach((side) => {
            const lens = new THREE.Mesh(
                new THREE.CylinderGeometry(0.2, 0.2, 0.06, 6, 1),
                lensMat
            );
            lens.rotation.x = Math.PI / 2;
            lens.rotation.z = Math.PI / 6;
            lens.scale.set(1.1, 1, 0.85);
            lens.position.set(
                side * 0.34,
                0.2,
                surfaceZ(side * 0.34, 0.2) + 0.04
            );
            group.add(lens);
            const shine = new THREE.Mesh(
                new THREE.PlaneGeometry(0.05, 0.2),
                new THREE.MeshBasicMaterial({
                    color: 0xffffff,
                    transparent: true,
                    opacity: 0.6,
                })
            );
            shine.position.set(
                side * 0.34 - 0.07,
                0.24,
                surfaceZ(side * 0.34, 0.2) + 0.075
            );
            shine.rotation.z = 0.5;
            group.add(shine);
        });
        const bridge = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.045, 0.045),
            lensMat
        );
        bridge.position.set(0, 0.25, surfaceZ(0, 0.25) + 0.03);
        group.add(bridge);
        return { group };
    }
    if (kind === 'hearts') {
        const pulse: THREE.Object3D[] = [];
        const geo = new THREE.ExtrudeGeometry(heartShape, {
            depth: 0.12,
            bevelEnabled: true,
            bevelSize: 0.05,
            bevelThickness: 0.05,
            bevelSegments: 4,
            curveSegments: 16,
        });
        geo.center();
        const mat = gloss(0xff2d6f, {
            emissive: 0xff2d6f,
            emissiveIntensity: 0.25,
        });
        [-1, 1].forEach((side) => {
            const h = new THREE.Mesh(geo, mat);
            h.scale.setScalar(0.28);
            h.position.set(
                side * 0.34,
                0.22,
                surfaceZ(side * 0.34, 0.22) + 0.08
            );
            h.rotation.z = side * -0.15;
            h.userData.baseScale = 0.28;
            group.add(h);
            pulse.push(h);
        });
        return { group, pulse };
    }
    return { group };
};

export const buildMouth = (kind: MouthId): FeatureBuild => {
    const group = new THREE.Group();
    if (kind === 'smile' || kind === 'tongue') {
        const arc = new THREE.Mesh(
            new THREE.TorusGeometry(0.28, 0.034, 12, 40, Math.PI * 0.8),
            std(0x3a0d0d, { roughness: 0.4 })
        );
        arc.rotation.z = Math.PI + (Math.PI - Math.PI * 0.8) / 2;
        arc.position.set(0, -0.08, surfaceZ(0, -0.25) + 0.015);
        arc.scale.set(1, 0.9, 1);
        group.add(arc);
        if (kind === 'tongue') {
            const tongue = new THREE.Mesh(
                new THREE.CapsuleGeometry(0.1, 0.1, 6, 14),
                gloss(0xff6f91)
            );
            tongue.position.set(0.1, -0.4, surfaceZ(0.1, -0.4) + 0.04);
            tongue.rotation.z = 0.15;
            tongue.scale.set(1, 1, 0.5);
            group.add(tongue);
            const line = new THREE.Mesh(
                new THREE.BoxGeometry(0.014, 0.12, 0.01),
                std(0xc2456a)
            );
            line.position.set(0.1, -0.4, surfaceZ(0.1, -0.4) + 0.095);
            line.rotation.z = 0.15;
            group.add(line);
        }
    } else if (kind === 'mustache') {
        const mat = std(0x3b2314, { roughness: 0.8 });
        [-1, 1].forEach((side) => {
            const half = new THREE.Mesh(
                new THREE.CapsuleGeometry(0.075, 0.28, 6, 14),
                mat
            );
            half.rotation.z = Math.PI / 2 - side * 0.35;
            half.position.set(
                side * 0.17,
                -0.2,
                surfaceZ(side * 0.17, -0.2) + 0.05
            );
            half.scale.set(1, 1, 0.65);
            group.add(half);
            const tip = new THREE.Mesh(
                new THREE.SphereGeometry(0.06, 12, 10),
                mat
            );
            tip.position.set(
                side * 0.37,
                -0.12,
                surfaceZ(side * 0.37, -0.12) + 0.04
            );
            group.add(tip);
        });
    }
    return { group };
};

/** Hats sit on the upper right, tilted, so the vine can still peek out. `home` is where it is parented. */
export const buildHat = (kind: HatId): FeatureBuild => {
    const group = new THREE.Group();
    if (kind === 'party') {
        const cone = new THREE.Mesh(
            new THREE.ConeGeometry(0.3, 0.75, 32, 1, true),
            std(0x7c3aed, { side: THREE.DoubleSide })
        );
        cone.position.y = 0.37;
        group.add(cone);
        const stripeMat = std(0xffd93d);
        [0.15, 0.32, 0.5].forEach((y) => {
            const r = 0.3 * (1 - y / 0.75) + 0.006;
            const ring = new THREE.Mesh(
                new THREE.TorusGeometry(r, 0.018, 8, 36),
                stripeMat
            );
            ring.rotation.x = Math.PI / 2;
            ring.position.y = y;
            group.add(ring);
        });
        const pom = new THREE.Mesh(
            new THREE.SphereGeometry(0.08, 16, 12),
            std(0xff5fa2)
        );
        pom.position.y = 0.78;
        group.add(pom);
    } else if (kind === 'tophat') {
        const mat = gloss(0x16161d, { roughness: 0.35 });
        const brim = new THREE.Mesh(
            new THREE.CylinderGeometry(0.52, 0.52, 0.045, 40),
            mat
        );
        group.add(brim);
        const crown = new THREE.Mesh(
            new THREE.CylinderGeometry(0.31, 0.34, 0.5, 40),
            mat
        );
        crown.position.y = 0.27;
        group.add(crown);
        const band = new THREE.Mesh(
            new THREE.CylinderGeometry(0.345, 0.345, 0.1, 40),
            std(0xe63946)
        );
        band.position.y = 0.1;
        group.add(band);
    } else if (kind === 'wizard') {
        const robe = std(0x2f2a8c, { side: THREE.DoubleSide });
        const brim = new THREE.Mesh(
            new THREE.CylinderGeometry(0.5, 0.5, 0.04, 40),
            robe
        );
        group.add(brim);
        const lower = new THREE.Mesh(
            new THREE.ConeGeometry(0.33, 0.6, 32, 1, true),
            robe
        );
        lower.position.y = 0.32;
        group.add(lower);
        // The tip folds over to one side.
        const tipPivot = new THREE.Group();
        tipPivot.position.y = 0.58;
        tipPivot.rotation.z = -0.55;
        const tip = new THREE.Mesh(
            new THREE.ConeGeometry(0.17, 0.5, 24, 1, true),
            robe
        );
        tip.position.y = 0.24;
        tipPivot.add(tip);
        group.add(tipPivot);
        const band = new THREE.Mesh(
            new THREE.TorusGeometry(0.335, 0.03, 8, 40),
            std(0xffc83d)
        );
        band.rotation.x = Math.PI / 2;
        band.position.y = 0.06;
        group.add(band);
        const starMat = std(0xffe27a, { emissive: 0x6b5a10 });
        [
            [0.0, 0.3, 0.24],
            [0.2, 0.2, -0.18],
            [-0.2, 0.42, -0.1],
        ].forEach(([x, y, z]) => {
            const star = new THREE.Mesh(
                new THREE.OctahedronGeometry(0.045),
                starMat
            );
            star.position.set(x, y, z);
            group.add(star);
        });
    } else if (kind === 'crown') {
        const gold = std(0xf2b630, {
            metalness: 0.35,
            roughness: 0.4,
            emissive: 0x6b4a08,
            emissiveIntensity: 0.55,
        });
        const band = new THREE.Mesh(
            new THREE.CylinderGeometry(0.34, 0.31, 0.2, 32, 1, true),
            gold
        );
        (band.material as THREE.Material).side = THREE.DoubleSide;
        band.position.y = 0.1;
        group.add(band);
        for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2;
            const spike = new THREE.Mesh(
                new THREE.ConeGeometry(0.1, 0.2, 5),
                gold
            );
            spike.position.set(Math.cos(a) * 0.32, 0.29, Math.sin(a) * 0.32);
            group.add(spike);
            const gem = new THREE.Mesh(
                new THREE.SphereGeometry(0.04, 10, 8),
                std([0xff2d6f, 0x3a86ff, 0x2ec4b6, 0xff6b35, 0x8ac926][i], {
                    emissive: 0x222222,
                })
            );
            gem.position.set(Math.cos(a) * 0.335, 0.1, Math.sin(a) * 0.335);
            group.add(gem);
        }
    }
    return { group };
};

/** A point on a loop around the tomato's lower body; the front (+Z) hangs lower by `dip`. */
const neckPoint = (a: number, y0: number, dip: number, lift = 0.06) => {
    const y = y0 - dip * Math.max(0, Math.cos(a)) ** 1.6;
    const r = Math.sqrt(Math.max(0, 1 - (y / BODY_Y_SCALE) ** 2)) + lift;
    return new THREE.Vector3(r * Math.sin(a), y, r * Math.cos(a));
};

const neckLoop = (y0: number, dip: number, lift?: number, n = 64) =>
    Array.from({ length: n }, (_, i) =>
        neckPoint((i / n) * Math.PI * 2, y0, dip, lift)
    );

const goldText = (text: string): THREE.CanvasTexture => {
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 128;
    const g = c.getContext('2d');
    if (g) {
        g.fillStyle = '#16161d';
        g.fillRect(0, 0, 256, 128);
        g.font = '900 78px "Baloo 2", Impact, sans-serif';
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        const grad = g.createLinearGradient(0, 30, 0, 100);
        grad.addColorStop(0, '#fff3b0');
        grad.addColorStop(0.5, '#ffc83d');
        grad.addColorStop(1, '#c98a00');
        g.fillStyle = grad;
        g.fillText(text, 128, 68);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
};

export const buildNeck = (kind: NeckId): FeatureBuild => {
    const group = new THREE.Group();
    if (kind === 'tshirt') {
        // A shirt shell hugging the lower body: scooped at the front so the face stays clear, with a hem
        // and two empty short sleeves poking out at the sides.
        const SHELL = 1.06;
        const HEM = -0.8;
        const cols = 72;
        const rows = 14;
        const navy = new THREE.Color(0x24407a);
        const white = new THREE.Color(0xf6f3ec);
        const positions: number[] = [];
        const colors: number[] = [];
        const indices: number[] = [];
        const edge: THREE.Vector3[] = [];
        const shellR = (y: number) =>
            Math.sqrt(Math.max(0.0004, 1 - (y / BODY_Y_SCALE) ** 2)) * SHELL;
        for (let i = 0; i <= cols; i++) {
            const a = (i / cols) * Math.PI * 2;
            const around = Math.abs(a > Math.PI ? a - Math.PI * 2 : a);
            const t = THREE.MathUtils.clamp((around - 0.35) / 1.3, 0, 1);
            const yTop = -0.5 + t * t * (3 - 2 * t);
            for (let j = 0; j <= rows; j++) {
                const y = yTop + (HEM - yTop) * (j / rows);
                const r = shellR(y);
                positions.push(r * Math.sin(a), y, r * Math.cos(a));
                const c = Math.floor((y + 1) / 0.16) % 2 ? navy : white;
                colors.push(c.r, c.g, c.b);
            }
            edge.push(
                new THREE.Vector3(
                    shellR(yTop) * Math.sin(a),
                    yTop,
                    shellR(yTop) * Math.cos(a)
                )
            );
        }
        for (let i = 0; i < cols; i++) {
            for (let j = 0; j < rows; j++) {
                const p = i * (rows + 1) + j;
                const q = p + rows + 1;
                indices.push(p, q, p + 1, q, q + 1, p + 1);
            }
        }
        const shellGeo = new THREE.BufferGeometry();
        shellGeo.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        );
        shellGeo.setAttribute(
            'color',
            new THREE.Float32BufferAttribute(colors, 3)
        );
        shellGeo.setIndex(indices);
        shellGeo.computeVertexNormals();
        group.add(
            new THREE.Mesh(
                shellGeo,
                std(0xffffff, {
                    vertexColors: true,
                    side: THREE.DoubleSide,
                    roughness: 0.85,
                })
            )
        );
        group.add(
            new THREE.Mesh(
                new THREE.TubeGeometry(
                    new THREE.CatmullRomCurve3(edge.slice(0, cols), true),
                    cols,
                    0.03,
                    8,
                    true
                ),
                std(navy.getHex(), { roughness: 0.8 })
            )
        );
        // Sleeves: open tubes angled out and slightly down, with a navy cuff.
        const tilt = 0.5;
        [-1, 1].forEach((side) => {
            const dir = new THREE.Vector3(
                side * Math.cos(tilt),
                -Math.sin(tilt),
                0
            );
            const start = new THREE.Vector3(side * 0.93, 0.3, 0);
            const sleeve = new THREE.Mesh(
                new THREE.CylinderGeometry(0.2, 0.17, 0.3, 20, 1, true),
                std(0xf6f3ec, { side: THREE.DoubleSide, roughness: 0.85 })
            );
            sleeve.rotation.z = -side * (Math.PI / 2 + tilt);
            sleeve.position.copy(start).addScaledVector(dir, 0.14);
            group.add(sleeve);
            const cuff = new THREE.Mesh(
                new THREE.CylinderGeometry(0.205, 0.205, 0.05, 20, 1, true),
                std(navy.getHex(), { side: THREE.DoubleSide, roughness: 0.8 })
            );
            cuff.rotation.z = sleeve.rotation.z;
            cuff.position.copy(start).addScaledVector(dir, 0.29);
            group.add(cuff);
        });
    } else if (kind === 'pearls') {
        const pearl = gloss(0xfaf3ea, {
            roughness: 0.15,
            clearcoat: 1,
            sheen: 1,
            sheenColor: new THREE.Color(0xffd6e8),
        });
        const geo = new THREE.SphereGeometry(0.075, 16, 12);
        geo.userData.shared = true;
        neckLoop(-0.52, 0.2, 0.07, 26).forEach((pos) => {
            const bead = new THREE.Mesh(geo, pearl);
            bead.position.copy(pos);
            group.add(bead);
        });
    } else if (kind === 'bling') {
        const gold = gloss(0xffc83d, { metalness: 1, roughness: 0.18 });
        const chain = new THREE.Mesh(
            new THREE.TubeGeometry(
                new THREE.CatmullRomCurve3(neckLoop(-0.5, 0.22, 0.05), true),
                96,
                0.03,
                8,
                true
            ),
            gold
        );
        group.add(chain);
        const front = neckPoint(0, -0.5, 0.22, 0.05);
        const pendant = new THREE.Group();
        pendant.position.set(0, front.y - 0.17, front.z + 0.03);
        pendant.rotation.x = 0.55;
        pendant.add(
            new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.34, 0.06), gold)
        );
        const face = new THREE.Mesh(
            new THREE.PlaneGeometry(0.54, 0.27),
            new THREE.MeshBasicMaterial({ map: goldText('2ma2') })
        );
        face.position.z = 0.032;
        pendant.add(face);
        const stone = std(0xbfefff, { emissive: 0x335566, roughness: 0.1 });
        [-0.3, 0.3].forEach((x) =>
            [-0.16, 0.16].forEach((y) => {
                const gem = new THREE.Mesh(
                    new THREE.OctahedronGeometry(0.045),
                    stone
                );
                gem.position.set(x, y, 0.04);
                pendant.add(gem);
            })
        );
        group.add(pendant);
    }
    return { group };
};

export interface FaceLayout {
    /** Added to y after scaling. */
    dy: number;
    /** Scales positions and sizes about the face's own origin. */
    s: number;
    /** Extra distance off the surface, e.g. to sit above a shirt. */
    lift?: number;
}

/** Moves and shrinks an eyes/mouth build, re-seating each piece on the curved surface. */
export const applyLayout = (
    build: FeatureBuild,
    { dy, s, lift = 0 }: FaceLayout
): FeatureBuild => {
    build.group.children.forEach((c) => {
        const { x, y, z } = c.position;
        const off = z - surfaceZ(x, y);
        const nx = x * s;
        const ny = y * s + dy;
        c.position.set(nx, ny, surfaceZ(nx, ny) + off * s + lift);
        c.scale.multiplyScalar(s);
        if (c.userData.faceNormal)
            c.lookAt(c.position.clone().multiplyScalar(2));
        if (typeof c.userData.baseScale === 'number') c.userData.baseScale *= s;
    });
    return build;
};

/** The kitchen-timer face: eyes above the band, a small mouth below it. */
export const TIMER_EYES: FaceLayout = { dy: 0.39, s: 0.8 };
export const TIMER_MOUTH: FaceLayout = { dy: -0.31, s: 0.55 };

/** The dial's span: a classic kitchen timer reads 0 to 60 minutes. */
export const DIAL_MINUTES = 60;

export interface DialBuild {
    group: THREE.Group;
    /** Turns the band so the pointer reads `fraction` (0..1) of the 60-minute scale. */
    setRemaining: (fraction: number) => void;
    /** The band is the body's colour. */
    setColor: (color: string) => void;
}

const BAND_HALF = 0.2;

/** Radius of the body's surface at height y (the lobes add up to 3.5%). */
const bodyR = (y: number) =>
    Math.sqrt(Math.max(0, 1 - (y / BODY_Y_SCALE) ** 2));

/** The band's profile: a rounded ridge that stands proud of the body, thicker in the middle. */
const bandR = (y: number) => {
    const k = Math.min(1, Math.abs(y) / BAND_HALF);
    return bodyR(y) + 0.05 + 0.04 * Math.sqrt(1 - k * k);
};

/**
 * The scale printed on the band: minutes, one tick each, a number every five. A lathe's u = 0 is the front and
 * grows towards +x, so painting value k at u = (60 - k) / 60 makes the numbers fall away to the right, as on a
 * real timer. White on transparent; the band underneath supplies the colour.
 */
const bandTexture = (maxAnisotropy: number): THREE.CanvasTexture => {
    const w = 4096;
    const h = 256;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    if (g) {
        g.clearRect(0, 0, w, h);
        g.fillStyle = '#ffffff';
        g.strokeStyle = '#ffffff';
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        g.font = '800 110px "Baloo 2", Arial, sans-serif';
        for (let k = 0; k < 60; k++) {
            const big = k % 5 === 0;
            g.lineWidth = big ? 12 : 7;
            // Drawn a lap either side as well: the 0 sits on the texture's seam, so each half has to land
            // on its own edge to meet up across it.
            for (const lap of [-1, 0, 1]) {
                const x = ((60 - k) / 60) * w + lap * w;
                g.beginPath();
                g.moveTo(x, h * 0.16);
                g.lineTo(x, h * (big ? 0.4 : 0.32));
                g.stroke();
                if (big) g.fillText(String(k), x, h * 0.66);
            }
        }
    }
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = maxAnisotropy;
    return tex;
};

/**
 * A classic kitchen timer: a solid ridged band round the tomato's middle carrying the minutes scale, a fixed
 * white pointer above it at the front, and the whole band turning as time runs out so the number under the
 * pointer counts down.
 */
export const buildDial = (maxAnisotropy = 8): DialBuild => {
    const group = new THREE.Group();

    const profile: THREE.Vector2[] = [];
    const steps = 24;
    for (let i = 0; i <= steps; i++) {
        const y = -BAND_HALF + (i / steps) * BAND_HALF * 2;
        profile.push(new THREE.Vector2(bandR(y), y));
    }
    const lathe = (offset: number) =>
        new THREE.LatheGeometry(
            profile.map((p) => new THREE.Vector2(p.x + offset, p.y)),
            192
        );

    // The band and its scale are one piece, so they turn together.
    const spin = new THREE.Group();
    const bandMat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.4,
        clearcoat: 0.5,
        side: THREE.DoubleSide,
    });
    spin.add(new THREE.Mesh(lathe(0), bandMat));
    const marks = new THREE.Mesh(
        lathe(0.003),
        new THREE.MeshBasicMaterial({
            map: bandTexture(maxAnisotropy),
            transparent: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
        })
    );
    spin.add(marks);
    group.add(spin);

    // Seams above and below, so the band reads as a separate ring set into the body. They follow the body's
    // five lobes, otherwise they sink into the surface in places and seem to fade.
    const seamMat = new THREE.MeshBasicMaterial({
        color: 0x2a0606,
        transparent: true,
        opacity: 0.5,
    });
    [-1, 1].forEach((side) => {
        const y = side * (BAND_HALF + 0.012);
        const lobeAmount = 1 - (y / BODY_Y_SCALE) ** 2;
        const points = Array.from({ length: 96 }, (_, i) => {
            const a = (i / 96) * Math.PI * 2;
            const r =
                bodyR(y) * (1 + 0.035 * Math.cos(a * 5) * lobeAmount) + 0.012;
            return new THREE.Vector3(r * Math.cos(a), y, r * Math.sin(a));
        });
        group.add(
            new THREE.Mesh(
                new THREE.TubeGeometry(
                    new THREE.CatmullRomCurve3(points, true),
                    192,
                    0.014,
                    6,
                    true
                ),
                seamMat
            )
        );
    });

    const tri = new THREE.Shape();
    tri.moveTo(-0.075, 0.09);
    tri.lineTo(0.075, 0.09);
    tri.lineTo(0, -0.07);
    tri.closePath();
    const pointer = new THREE.Mesh(
        new THREE.ShapeGeometry(tri),
        gloss(0xffffff, { roughness: 0.3, side: THREE.DoubleSide })
    );
    pointer.position.set(0, 0.34, surfaceZ(0, 0.34) + 0.035);
    pointer.rotation.x = -0.35;
    group.add(pointer);

    return {
        group,
        setColor: (color) => {
            bandMat.color.set(color);
        },
        setRemaining: (fraction) => {
            const f = THREE.MathUtils.clamp(fraction, 0, 1);
            // The number under the pointer is the content at body angle -rotation.
            spin.rotation.y = f * Math.PI * 2;
        },
    };
};
